import { Fighter } from './fighter';
import type { FightRules } from './rules';
import { Signal } from './signal';
import { FighterStats, other } from './types';
import type { BoutPhase, BoutResult, FightCommand, Outcome, PunchReport, PunchStats, Side, Winner } from './types';
import { isPunch } from './types';

/**
 * The referee: two fighters, the rounds and their clock, what each punch does when it arrives, the knockout, the
 * decision and the numbers for the end. It knows nothing about what is on screen.
 */
export class Bout {
  readonly punchStarted = new Signal<{ side: Side; punch: PunchStats }>();
  readonly punchResolved = new Signal<PunchReport>();
  readonly punchInterrupted = new Signal<{ side: Side; punch: PunchStats }>();
  readonly knockedOut = new Signal<Side>();
  readonly roundStarted = new Signal<number>();
  readonly roundEnded = new Signal<number>();
  readonly over = new Signal<BoutResult>();

  phase: BoutPhase = 'round';
  round = 1;
  /** Seconds left in the round, or in the rest before the next one. */
  secondsLeft: number;
  result: BoutResult | null = null;

  private readonly stats: Record<Side, FighterStats> = { player: new FighterStats(), opponent: new FighterStats() };

  constructor(readonly rules: FightRules, readonly player: Fighter, readonly opponent: Fighter) {
    this.secondsLeft = rules.roundSeconds;
    player.punchLanded.add((punch) => this.resolve('player', punch));
    opponent.punchLanded.add((punch) => this.resolve('opponent', punch));
    player.interrupted.add((punch) => this.punchInterrupted.emit({ side: 'player', punch }));
    opponent.interrupted.add((punch) => this.punchInterrupted.emit({ side: 'opponent', punch }));
  }

  fighter(side: Side): Fighter {
    return side === 'player' ? this.player : this.opponent;
  }

  statsOf(side: Side): FighterStats {
    return this.stats[side];
  }

  /** True when the fighter does it; outside a round nobody does anything. */
  submit(side: Side, command: FightCommand): boolean {
    if (this.phase !== 'round') return false;
    const fighter = this.fighter(side);
    if (!fighter.submit(command)) return false;
    if (isPunch(command.action)) {
      this.stats[side].thrown++;
      this.punchStarted.emit({ side, punch: fighter.currentPunch!.stats });
    }
    return true;
  }

  tick(seconds: number): void {
    switch (this.phase) {
      case 'rest':
        this.secondsLeft -= seconds;
        if (this.secondsLeft <= 0) this.startRound(this.round + 1);
        break;
      case 'round':
        this.player.tick(seconds);
        this.opponent.tick(seconds);
        if (this.phase === 'round') {
          this.secondsLeft -= seconds;
          if (this.secondsLeft <= 0) this.endRound();
        }
        // After the fighters' own steps, never inside one: a knockout lands in the middle of the winner's punch.
        if (this.result) {
          this.player.standDown();
          this.opponent.standDown();
        }
        break;
    }
  }

  private resolve(attacker: Side, punch: PunchStats): void {
    if (this.phase !== 'round') return;
    const defender = other(attacker);
    const target = this.fighter(defender);
    let outcome: Outcome;
    let damage: number;
    if (target.isDodging && punch.target === 'head') {
      outcome = 'dodged';
      damage = 0;
      this.stats[defender].dodged++;
    } else if (target.state === 'blocking') {
      outcome = 'blocked';
      damage = punch.damage === 0 ? 0 : Math.max(1, Math.floor(punch.damage * this.rules.blockShare));
      this.stats[defender].blocked++;
    } else {
      outcome = 'clean';
      damage = punch.damage;
      this.stats[attacker].landed++;
      this.fighter(attacker).landedClean();
    }
    if (outcome !== 'dodged') target.takeHit(damage, outcome === 'clean', outcome === 'clean' && punch.staggers);
    this.stats[attacker].damageDealt += damage;
    this.punchResolved.emit({ attacker, punch, outcome, damage });
    if (target.state !== 'knockedOut') return;
    this.knockedOut.emit(defender);
    this.finish(attacker === 'player' ? 'player' : 'opponent', true);
  }

  private endRound(): void {
    this.roundEnded.emit(this.round);
    if (this.round >= this.rules.rounds) {
      const player = this.stats.player.damageDealt;
      const opponent = this.stats.opponent.damageDealt;
      this.finish(player > opponent ? 'player' : opponent > player ? 'opponent' : 'nobody', false);
      return;
    }
    this.phase = 'rest';
    this.secondsLeft = this.rules.restSeconds;
    this.player.rest(this.rules.recoveryShare);
    this.opponent.rest(this.rules.recoveryShare);
  }

  private startRound(round: number): void {
    this.round = round;
    this.phase = 'round';
    this.secondsLeft = this.rules.roundSeconds;
    this.roundStarted.emit(round);
  }

  private finish(winner: Winner, byKnockout: boolean): void {
    this.phase = 'over';
    this.result = { winner, byKnockout, round: this.round, player: this.stats.player, opponent: this.stats.opponent };
    this.over.emit(this.result);
  }
}
